import { pascalCaseToWords } from '../../../app/common/common-utils';
import { EntityType } from '../../enums/entity-type';
import { EntityPropertyUpdateSynchronisationInfo } from './entity-property-update-synchronisation-info';
import { EntityPropertyUpdatePropagationInfo } from './internal/entity-property-update-propagation-info';

export class PropertyUpdateSynchronisationInfo {
  private readonly _changedMachineFields?: string[];
  private readonly _changedDocumentationFields?: string[];
  private readonly _entityPropertyUpdateSynchronisationInfos?: EntityPropertyUpdateSynchronisationInfo[];

  private currentFieldType?: 'machine' | 'documentation';

  constructor(
    changedMachineFields?: string[],
    changedDocumentationFields?: string[],
    entityPropertyUpdateSynchronisationInfos?: EntityPropertyUpdateSynchronisationInfo[]
  ) {
    this._changedMachineFields = changedMachineFields;
    this._changedDocumentationFields = changedDocumentationFields;
    this._entityPropertyUpdateSynchronisationInfos = entityPropertyUpdateSynchronisationInfos;
  }

  public get changedMachineFields(): string[] | undefined {
    return this._changedMachineFields?.map((field) => pascalCaseToWords(field));
  }

  public get changedDocumentationFields(): string[] | undefined {
    return this._changedDocumentationFields?.map((field) => pascalCaseToWords(field));
  }

  public get entityPropertyUpdateSynchronisationInfos(): EntityPropertyUpdateSynchronisationInfo[] | undefined {
    return this._entityPropertyUpdateSynchronisationInfos;
  }

  public get steps(): number {
    const machineFieldsAffected = this.changedMachineFields !== undefined && this.changedMachineFields.length > 0;
    const documentationFieldsAffected =
      this.changedDocumentationFields !== undefined && this.changedDocumentationFields.length > 0;
    return machineFieldsAffected && documentationFieldsAffected
      ? 2
      : !machineFieldsAffected && !documentationFieldsAffected
        ? 0
        : 1;
  }

  public get entityPropertyUpdateSynchronisationInfosForCurrentStep(): EntityPropertyUpdateSynchronisationInfo[] {
    if (this.currentFieldType === 'machine') {
      return (
        this._entityPropertyUpdateSynchronisationInfos?.filter((info) => info.machineFieldsAffected === true) ?? []
      );
    } else if (this.currentFieldType === 'documentation') {
      return (
        this._entityPropertyUpdateSynchronisationInfos?.filter((info) => info.documentationFieldsAffected === true) ??
        []
      );
    } else {
      return [];
    }
  }

  public get anyFieldsAffected(): boolean {
    return (
      (this.changedMachineFields !== undefined && this.changedMachineFields.length > 0) ||
      (this.changedDocumentationFields !== undefined && this.changedDocumentationFields.length > 0)
    );
  }

  /**
   * Returns true if only the edited entity is affected.
   */
  public get isOnlyEntityEditedAffected(): boolean {
    return this.entityPropertyUpdateSynchronisationInfos?.every((info) => info.isEditedEntity) ?? false;
  }

  // TODO: We should consider to work without steps as number.
  /**
   * Returns the changed fields for the given step number. If there are only changed machine fields or only changed documentation fields, step number should be 1. If there are both changed machine fields and changed documentation fields, step number should be either 1 or 2. For step number 1, changed machine fields are returned if they exist, otherwise changed documentation fields are returned. For step number 2, changed documentation fields are returned. If there are no changed machine fields and no changed documentation fields, an error is thrown regardless of the step number.
   * @param step The step number for which to retrieve the changed fields. The step number can be get from the **steps** property.
   * @returns An array of changed fields for the given step.
   */
  public getFieldsByStep(step: number): string[] {
    if (this.changedDocumentationFields === undefined && this.changedMachineFields === undefined) {
      throw new Error('Changed fields are not defined.');
    }

    if (step === 1) {
      if (this.changedMachineFields !== undefined && this.changedMachineFields.length > 0) {
        this.currentFieldType = 'machine';
        return this.changedMachineFields;
      } else if (this.changedDocumentationFields !== undefined && this.changedDocumentationFields.length > 0) {
        this.currentFieldType = 'documentation';
        return this.changedDocumentationFields;
      } else {
        this.currentFieldType = undefined;
        return [];
      }
    } else if (
      step === 2 &&
      this.changedDocumentationFields !== undefined &&
      this.changedDocumentationFields.length > 0
    ) {
      this.currentFieldType = 'documentation';
      return this.changedDocumentationFields;
    } else {
      throw new Error(
        'Invalid step number. Step number should be either 1 or 2. If there are only changed machine fields or only changed documentation fields, step number should be 1.'
      );
    }
  }

  public getFieldNameForStep(step: number): string {
    if (step === 1) {
      if (this.changedMachineFields !== undefined && this.changedMachineFields.length > 0) {
        return 'Machine fields';
      } else if (this.changedDocumentationFields !== undefined && this.changedDocumentationFields.length > 0) {
        return 'Documentation fields';
      }
    } else if (
      step === 2 &&
      this.changedDocumentationFields !== undefined &&
      this.changedDocumentationFields.length > 0
    ) {
      return 'Documentation fields';
    }
    throw new Error('Invalid step number or no fields affected for the given step.');
  }

  public confirmUpdatedValueToggle(entityId: string, propertyName: string, isChecked: boolean): void {
    const propertyInfo = this.entityPropertyUpdateSynchronisationInfos
      ?.find((info) => info.entityId === entityId && info.getPropertyByName(propertyName) !== undefined)
      ?.getPropertyByName(propertyName);
    if (propertyInfo) {
      propertyInfo.updatedValueIsConfirmed = isChecked;
    } else {
      console.warn(`Property with name ${propertyName} for entity with id ${entityId} not found.`);
    }
  }

  public confirmAllUpdatedValuesForPropertyNameToggle(propertyName: string, isChecked: boolean): void {
    this.entityPropertyUpdateSynchronisationInfos
      ?.filter((info) => info.getPropertyByName(propertyName) !== undefined)
      .forEach((info) => {
        const propertyInfo = info.getPropertyByName(propertyName);
        if (propertyInfo) {
          propertyInfo.updatedValueIsConfirmed = isChecked;
        }
      });
  }

  public getPropertyUpdatePropagationInfo(): EntityPropertyUpdatePropagationInfo[] {
    return (
      this.entityPropertyUpdateSynchronisationInfos
        ?.filter((info) => !info.isEditedEntity)
        ?.map((info) => ({
          entityId: info.entityId,
          entityType: info.entityType ?? EntityType.None,
          propertiesToUpdate:
            info.properties
              ?.filter((property) => property.updatedValueIsConfirmed)
              .map((property) => property.propertyName ?? '') ?? [],
        })) ?? []
    );
  }
}
